import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useSeo } from "@/hooks/useSeo";
import { getSeriesById, seriesList } from "@/mocks/series";

export default function Play() {
  const { id = "" } = useParams();
  const series = getSeriesById(id);
  const [currentEpisode, setCurrentEpisode] = useState(1);
  const [notice, setNotice] = useState("");

  useSeo({
    title: series ? `${series.title} 剧集播放演示 - 仙剑奇侠传影视站` : "播放演示 - 仙剑奇侠传影视站",
    description: series
      ? `《${series.title}》剧集播放界面演示，支持选集切换效果展示。本页为前端 UI 演示，不含真实视频资源。`
      : "仙剑奇侠传影视站播放演示页面。",
    keywords: "仙剑奇侠传播放,仙侠剧播放演示,剧集选集,仙剑奇侠传在线观看",
  });

  const episodes = useMemo(() => {
    const total = series?.episodes ?? 0;
    return Array.from({ length: Math.min(total, 60) }, (_, index) => index + 1);
  }, [series]);

  const recommendations = useMemo(
    () => seriesList.filter((item) => item.id !== id).slice(0, 4),
    [id]
  );

  const showNotice = (text: string) => {
    setNotice(text);
    window.setTimeout(() => setNotice(""), 2200);
  };

  if (!series) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex min-h-[70vh] items-center justify-center px-4 pt-16 md:pt-20">
          <div className="text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
              <i className="ri-play-circle-line text-3xl"></i>
            </span>
            <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
              暂无播放资源
            </h1>
            <p className="mt-3 max-w-md text-sm text-foreground-600">
              没有找到对应的剧集播放演示，请返回分类页选择其他作品。
            </p>
            <Link
              to="/category"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-layout-grid-line"></i>
              返回分类页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16 md:pt-20">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto flex w-full max-w-[1200px] flex-wrap items-center justify-between gap-3 px-4 py-5 md:px-6">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/detail/${series.id}`} className="hover:text-primary-600">
                {series.title}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">播放演示</span>
            </nav>
            <Link
              to={`/detail/${series.id}`}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
            >
              <i className="ri-arrow-left-line"></i>
              返回详情页
            </Link>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
            <div className="lg:col-span-2">
              <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-background-200 bg-primary-950">
                <img
                  src={series.backdrop}
                  alt={`${series.title} 播放演示画面`}
                  title={`${series.title} 播放界面`}
                  className="h-full w-full object-cover object-top opacity-55"
                />
                <div className="absolute inset-0 bg-primary-950/45"></div>
                <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
                  <button
                    type="button"
                    onClick={() => showNotice("演示模式：当前页面仅展示播放界面，暂不支持真实播放")}
                    className="flex h-20 w-20 cursor-pointer items-center justify-center rounded-full bg-accent-500 text-primary-950 transition-transform hover:scale-105"
                    aria-label="开始播放（演示）"
                  >
                    <i className="ri-play-fill text-4xl"></i>
                  </button>
                  <p className="mt-5 font-heading text-xl font-bold text-background-50 md:text-2xl">
                    {series.title}
                  </p>
                  <p className="mt-1.5 text-sm text-primary-100">
                    第 {currentEpisode} 集 · 全 {series.episodes} 集
                  </p>
                </div>
                <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-background-50/15 px-3 py-1 text-xs text-background-50 backdrop-blur">
                  <i className="ri-information-line"></i>
                  演示界面 · 无真实播放
                </span>
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-accent-300 bg-accent-50 px-4 py-3">
                <p className="text-sm text-accent-900">
                  <i className="ri-error-warning-line mr-1.5"></i>
                  本页面为纯前端 UI 演示，所有播放行为均为界面反馈，不含任何真实视频资源。
                </p>
              </div>

              <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  当前剧集信息
                </h2>
                <h3 className="mt-3 font-heading text-xl font-bold text-primary-700">
                  {series.title} · 第 {currentEpisode} 集
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                  {series.summary[0]}
                </p>
                <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-foreground-500">
                  <span>
                    <i className="ri-user-star-line mr-1"></i>
                    主演：{series.cast.join(" · ")}
                  </span>
                  <span>
                    <i className="ri-video-line mr-1"></i>
                    导演：{series.director}
                  </span>
                </div>
              </div>
            </div>

            <aside className="lg:col-span-1">
              <div className="rounded-lg border border-background-200 bg-background-50">
                <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
                  <h2 className="font-heading text-base font-bold text-foreground-950">选集列表</h2>
                  <span className="text-xs text-foreground-500">共 {series.episodes} 集</span>
                </div>
                <div className="max-h-[440px] overflow-y-auto p-4">
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-3">
                    {episodes.map((episode) => (
                      <button
                        key={episode}
                        type="button"
                        onClick={() => {
                          setCurrentEpisode(episode);
                          showNotice(`已切换到第 ${episode} 集（演示）`);
                        }}
                        className={`flex h-10 cursor-pointer items-center justify-center rounded-md text-sm transition-colors ${
                          episode === currentEpisode
                            ? "bg-primary-500 font-semibold text-background-50"
                            : "bg-background-100 text-foreground-700 hover:bg-background-200"
                        }`}
                      >
                        {episode}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-5 rounded-lg border border-background-200 bg-background-100 p-5">
                <h3 className="font-heading text-sm font-bold text-foreground-950">相关推荐</h3>
                <ul className="mt-4 space-y-3">
                  {recommendations.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={`/detail/${item.id}`}
                        className="group flex items-center gap-3"
                      >
                        <span className="h-16 w-12 shrink-0 overflow-hidden rounded-md bg-background-200">
                          <img
                            src={item.poster}
                            alt={item.title}
                            className="h-full w-full object-cover object-top"
                          />
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-foreground-900 group-hover:text-primary-700">
                            {item.title}
                          </span>
                          <span className="mt-1 block text-xs text-foreground-500">
                            {item.year} · {item.category}
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </main>

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 rounded-full bg-primary-900 px-5 py-2.5 text-center text-sm font-medium text-background-50">
          {notice}
        </div>
      )}

      <Footer />
    </div>
  );
}